/* Lucian UX/UI Prompt #070 · MIT License (c) 2026 Seokwoooo · https://lucianlabs.dev/uxui-prompt/#070 */
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import './lucian-070.css';

const ICON = {
  ok: '<svg viewBox="0 0 18 18"><path d="M4 9.4l3.2 3.2L14 5.6"/></svg>',
  err: '<svg viewBox="0 0 18 18"><path d="M9 5v5M9 13h.01"/></svg>',
  info: '<svg viewBox="0 0 18 18"><rect x="6" y="6" width="8.5" height="8.5" rx="2"/><path d="M11.5 6V5a1.5 1.5 0 0 0-1.5-1.5H5A1.5 1.5 0 0 0 3.5 5v5A1.5 1.5 0 0 0 5 11.5h1"/></svg>',
  wait: '<i></i>',
};
const COLOR = { ok: 'var(--ux-mint)', err: 'var(--ux-danger)', info: 'var(--ux-accent)', wait: 'var(--ux-warn)' };

// 원본 스크립트를 그대로 옮기고, 데모 버튼 대신 push()를 밖으로 노출한다.
// push({ kind, title, sub, action, onAction }) → { resolve(title, sub) } (wait 알림을 완료로 바꿀 때)
const LucianToasts = forwardRef(function LucianToasts(_, ref) {
  const rootRef = useRef(null);
  const api = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    const zone = root.querySelector('.u070-zone');
    const live = root.querySelector('.u070-live');
    const rm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timers = [];
    let list = [], open = false, raf = 0;
    const DUR = 4500;

    function layout() {
      let acc = 0;
      list.forEach((t, i) => {
        const el = t.el;
        el.classList.toggle('back', !open && i > 0);
        if (open) { el.style.setProperty('--y', -acc + 'px'); el.style.setProperty('--s', 1); el.style.setProperty('--o', 1); acc += el.offsetHeight + 8; }
        else { el.style.setProperty('--y', -(i * 14) + 'px'); el.style.setProperty('--s', (1 - i * 0.06).toFixed(2)); el.style.setProperty('--o', i > 2 ? 0 : 1); }
        el.style.zIndex = 50 - i;
        el.inert = !open && i > 0;
      });
    }
    function remove(t, dir) {
      const i = list.indexOf(t);
      if (i < 0) return;
      list.splice(i, 1);
      t.el.classList.add('leave');
      if (dir) { t.el.style.setProperty('--dir', dir); t.el.classList.add('side'); }
      timers.push(setTimeout(() => t.el.remove(), 450));
      layout();
    }
    function setText(t, kind, title, sub) {
      t.el.style.setProperty('--c', COLOR[kind]);
      t.el.querySelector('.u070-ic').innerHTML = ICON[kind];
      t.el.querySelector('b').textContent = title;
      t.el.querySelector('small').textContent = sub || '';
      t.el.querySelector('small').hidden = !sub;
      live.textContent = title;
    }
    function push({ kind = 'ok', title, sub, action, onAction }) {
      const el = document.createElement('div');
      el.className = 'u070-t enter';
      el.setAttribute('role', kind === 'err' ? 'alert' : 'status');
      el.innerHTML = '<span class="u070-ic" aria-hidden="true"></span><span class="u070-tx"><b></b><small></small></span>' + (action ? '<button class="u070-act" type="button"></button>' : '') + '<button class="u070-x" type="button" aria-label="알림 닫기"><svg viewBox="0 0 14 14"><path d="M3.5 3.5l7 7M10.5 3.5l-7 7"/></svg></button><span class="u070-bar" aria-hidden="true"></span>';
      const t = { el, left: kind === 'wait' ? Infinity : DUR };
      setText(t, kind, title, sub);
      if (action) el.querySelector('.u070-act').textContent = action;
      zone.appendChild(el);
      list.unshift(t);
      void el.offsetWidth;
      el.classList.remove('enter');
      layout();
      el.querySelector('.u070-x').addEventListener('click', () => remove(t));
      if (action) el.querySelector('.u070-act').addEventListener('click', () => { remove(t); onAction && onAction(); });
      // 옆으로 밀어 닫기
      let sx = 0, dx = 0, down = false;
      el.addEventListener('pointerdown', e => { if (e.target.closest('button')) return; down = true; sx = e.clientX; dx = 0; el.setPointerCapture(e.pointerId); el.classList.add('drag'); });
      el.addEventListener('pointermove', e => { if (!down) return; dx = e.clientX - sx; el.style.setProperty('--dx', dx + 'px'); el.style.setProperty('--o', Math.max(0.2, 1 - Math.abs(dx) / 260)); });
      const end = () => { if (!down) return; down = false; el.classList.remove('drag'); if (Math.abs(dx) > 90) remove(t, dx > 0 ? 1 : -1); else { el.style.setProperty('--dx', '0px'); layout(); } };
      el.addEventListener('pointerup', end); el.addEventListener('pointercancel', end);
      // 화면에 쌓이는 개수는 5개로 제한
      while (list.length > 5) remove(list[list.length - 1]);
      return {
        resolve(nextTitle, nextSub, nextKind = 'ok') {
          if (list.indexOf(t) < 0) return;
          setText(t, nextKind, nextTitle, nextSub);
          t.left = DUR;
          layout();
        },
        dismiss() { remove(t); },
      };
    }
    // 펼쳐 둔 동안은 남은 시간이 줄지 않습니다
    let last = performance.now();
    const loop = now => {
      const dt = now - last; last = now;
      if (!open) list.slice().forEach((t, i) => {
        if (i > 0 || t.left === Infinity) return;
        t.left -= dt;
        t.el.style.setProperty('--left', Math.max(0, t.left / DUR).toFixed(3));
        if (t.left <= 0) remove(t);
      });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const setOpen = v => { if (open === v) return; open = v; layout(); };
    // 도크 영역은 클릭을 통과시키므로(pointer-events:none) 알림 위에서 올라오는 이벤트로 판단
    zone.addEventListener('pointerover', e => { if (e.pointerType === 'mouse' && e.target.closest('.u070-t')) setOpen(true); });
    zone.addEventListener('pointerout', e => { if (e.pointerType === 'mouse' && !(e.relatedTarget && zone.contains(e.relatedTarget)) && !zone.contains(document.activeElement)) setOpen(false); });
    zone.addEventListener('focusin', () => setOpen(true));
    zone.addEventListener('focusout', e => { if (!zone.contains(e.relatedTarget)) setOpen(false); });
    zone.addEventListener('click', e => { if (!e.target.closest('button') && list.length > 1) setOpen(!open); });
    void rm;
    api.current = { push };
    return () => { cancelAnimationFrame(raf); timers.forEach(clearTimeout); api.current = null; };
  }, []);

  useImperativeHandle(ref, () => ({
    push: opts => (api.current ? api.current.push(opts) : { resolve() {}, dismiss() {} }),
  }), []);

  return (
    <div ref={rootRef} className="ux u070 u070-dock">
      <div className="u070-zone" role="region" aria-label="알림" tabIndex={-1}>
        <div className="u070-live" aria-live="polite"></div>
      </div>
    </div>
  );
});

export default LucianToasts;
